import {
  Mesh,
  MeshBasicMaterial,
  PointLight,
  SphereGeometry,
  Vector3,
} from 'three';
import { Pool } from '../util/pool.js';
import { lerp, rand } from '../util/math.js';
import { segmentIntersectsObstacle } from '../world/obstacles.js';

const defaultWeapon = {
  id: 'smg',
  pellets: 1,
  spread: 0.014,
  speed: 46,
  damage: 1.05,
  radius: 0.16,
  ttl: 1.22,
  pierce: 0,
  color: 0x53ffb5,
};

const geometry = new SphereGeometry(0.14, 12, 8);
const dirScratch = new Vector3();
const prevScratch = new Vector3();

let bulletState = null;

export function initBullets(world, options = {}) {
  const capacity = options.capacity ?? 192;
  const pool = new Pool(createBullet, resetBullet, Math.min(24, capacity), capacity);
  bulletState = {
    world,
    pool,
    bullets: [],
  };
  return bulletState;
}

export function getBullets() {
  if (!bulletState) {
    throw new Error('[bullets] initBullets() must run before getBullets().');
  }
  return bulletState.bullets;
}

export function spawnBullet(origin, aim, options = {}) {
  const state = bulletState;
  if (!state) throw new Error('[bullets] initBullets() must run before spawnBullet().');

  const weapon = { ...defaultWeapon, ...options.weapon };
  const overdrive = options.overdrive ? 1.25 : 1;
  const baseAngle = Math.atan2(aim.x, aim.z);
  const spawned = [];

  for (let i = 0; i < weapon.pellets; i += 1) {
    const spread = weapon.pellets === 1
      ? rand(-weapon.spread, weapon.spread)
      : lerp(-weapon.spread, weapon.spread, i / Math.max(1, weapon.pellets - 1)) + rand(-0.018, 0.018);
    const angle = baseAngle + spread;
    dirScratch.set(Math.sin(angle), 0, Math.cos(angle)).normalize();

    const bullet = state.pool.acquire();
    if (!bullet) continue;

    bullet.pos.copy(origin).addScaledVector(dirScratch, 1.18);
    bullet.pos.y = 1.12;
    bullet.vel.copy(dirScratch).multiplyScalar(weapon.speed * overdrive);
    bullet.age = 0;
    bullet.ttl = weapon.ttl;
    bullet.radius = weapon.radius;
    bullet.crit = Math.random() < (weapon.critChance || 0);
    bullet.damage = weapon.damage * (bullet.crit ? weapon.critMultiplier || 2.4 : 1);
    bullet.pierce = weapon.pierce || 0;
    bullet.ricochet = weapon.ricochet || 0;
    bullet.weapon = weapon.id;
    bullet.explode = weapon.explode || 0;
    bullet.freeze = weapon.freeze || 0;
    bullet.stagger = weapon.stagger || 0;
    bullet.execute = weapon.execute || 0;
    bullet.color = weapon.color;
    bullet.dead = false;
    bullet.hits.clear();
    bullet.mesh.material.color.setHex(weapon.color);
    bullet.mesh.position.copy(bullet.pos);
    bullet.mesh.scale.setScalar((weapon.radius / 0.14) * (weapon.id === 'rail' ? 1.22 : 1));
    bullet.mesh.visible = true;
    state.world.bulletRoot.add(bullet.mesh);
    state.bullets.push(bullet);
    spawned.push(bullet);
  }

  return spawned;
}

export function updateBullets(dt, options = {}) {
  const state = bulletState;
  if (!state) return;

  const {
    enemies = [],
    onHit = null,
    onExpire = null,
  } = options;

  for (const bullet of state.bullets) {
    bullet.age += dt;
    prevScratch.copy(bullet.pos);
    bullet.pos.addScaledVector(bullet.vel, dt);
    bullet.mesh.position.copy(bullet.pos);
    bullet.mesh.rotation.y += dt * 14;

    const obstacle = segmentIntersectsObstacle(prevScratch, bullet.pos, bullet.radius, state.world);
    if (obstacle) {
      if (bullet.ricochet > 0) {
        bounceBullet(bullet, obstacle, prevScratch);
        continue;
      }
      bullet.dead = true;
      onExpire?.(bullet);
      continue;
    }

    if (isOutOfBounds(bullet, state.world) || bullet.age > bullet.ttl) {
      bullet.dead = true;
      onExpire?.(bullet);
      continue;
    }

    for (const enemy of enemies) {
      if (!enemy.alive || bullet.hits.has(enemy)) continue;
      const dx = bullet.pos.x - enemy.pos.x;
      const dz = bullet.pos.z - enemy.pos.z;
      const radius = enemy.radius + bullet.radius;
      if (dx * dx + dz * dz >= radius * radius) continue;

      bullet.hits.add(enemy);
      onHit?.(enemy, bullet);
      if (bullet.pierce > 0) {
        bullet.pierce -= 1;
        bullet.vel.multiplyScalar(0.92);
      } else {
        bullet.dead = true;
        break;
      }
    }
  }

  for (let i = state.bullets.length - 1; i >= 0; i -= 1) {
    if (state.bullets[i].dead) releaseBullet(state.bullets[i], i);
  }
}

export function clearBullets() {
  const state = bulletState;
  if (!state) return;

  for (let i = state.bullets.length - 1; i >= 0; i -= 1) {
    releaseBullet(state.bullets[i], i);
  }
}

function createBullet() {
  const material = new MeshBasicMaterial({ color: defaultWeapon.color });
  const mesh = new Mesh(geometry, material);
  const light = new PointLight(defaultWeapon.color, 1.1, 4.5, 2.5);
  mesh.add(light);
  return {
    mesh,
    pos: new Vector3(),
    vel: new Vector3(),
    hits: new Set(),
    dead: true,
  };
}

function resetBullet(bullet) {
  bullet.mesh.visible = false;
  bullet.mesh.scale.setScalar(1);
  bullet.pos.set(0, 0, 0);
  bullet.vel.set(0, 0, 0);
  bullet.explode = 0;
  bullet.freeze = 0;
  bullet.stagger = 0;
  bullet.execute = 0;
  bullet.crit = false;
  bullet.ricochet = 0;
  bullet.color = defaultWeapon.color;
  bullet.hits.clear();
  bullet.dead = true;
}

function bounceBullet(bullet, obstacle, previous) {
  const aabb = obstacle.userData?.aabb;
  if (aabb) {
    const outsideX = previous.x < aabb.minX || previous.x > aabb.maxX;
    const outsideZ = previous.z < aabb.minZ || previous.z > aabb.maxZ;
    if (outsideX && !outsideZ) bullet.vel.x *= -1;
    else if (outsideZ && !outsideX) bullet.vel.z *= -1;
    else {
      const dx = Math.min(Math.abs(previous.x - aabb.minX), Math.abs(previous.x - aabb.maxX));
      const dz = Math.min(Math.abs(previous.z - aabb.minZ), Math.abs(previous.z - aabb.maxZ));
      if (dx < dz) bullet.vel.x *= -1;
      else bullet.vel.z *= -1;
    }
  } else {
    const dx = previous.x - obstacle.position.x;
    const dz = previous.z - obstacle.position.z;
    if (Math.abs(dx) > Math.abs(dz)) bullet.vel.x *= -1;
    else bullet.vel.z *= -1;
  }
  bullet.ricochet -= 1;
  bullet.pos.copy(previous).addScaledVector(bullet.vel.clone().normalize(), bullet.radius + 0.08);
  bullet.mesh.position.copy(bullet.pos);
}

function releaseBullet(bullet, index) {
  bulletState.world.bulletRoot.remove(bullet.mesh);
  bulletState.bullets.splice(index, 1);
  bulletState.pool.release(bullet);
}

function isOutOfBounds(bullet, world) {
  return Math.abs(bullet.pos.x) > world.half + 4 || Math.abs(bullet.pos.z) > world.half + 4;
}
